import type { SVGProps } from 'react';

export type StateIllustrationVariant =
  | 'verified-empty'
  | 'filtered-empty'
  | 'unavailable'
  | 'permission'
  | 'complete';

export interface StateIllustrationProps extends SVGProps<SVGSVGElement> {
  variant?: StateIllustrationVariant;
  title?: string;
}

const paths: Record<StateIllustrationVariant, React.ReactNode> = {
  'verified-empty': (
    <>
      <path d="M18 34h28M22 27h20M26 20h12" opacity=".34" />
      <path d="M19 42h26a5 5 0 0 0 5-5V17a5 5 0 0 0-5-5H19a5 5 0 0 0-5 5v20a5 5 0 0 0 5 5Z" />
      <circle cx="49" cy="43" r="7" fill="currentColor" stroke="none" opacity=".18" />
      <path d="m46.5 43 1.8 1.8 3.7-4.1" />
    </>
  ),
  'filtered-empty': (
    <>
      <path d="M13 14h38L37 30v13l-10 6V30L13 14Z" />
      <path d="M23 20h18" opacity=".4" />
      <circle cx="47" cy="44" r="7" fill="currentColor" stroke="none" opacity=".16" />
      <path d="m44.5 41.5 5 5m0-5-5 5" />
    </>
  ),
  unavailable: (
    <>
      <path d="M14 38c-3.6-8.4 2.2-17.2 11.3-17.2A13 13 0 0 1 50 26.4 9 9 0 0 1 48 44H20" />
      <path d="m18 50 32-36" />
      <path d="M27 44h21" opacity=".35" />
    </>
  ),
  permission: (
    <>
      <rect x="17" y="27" width="30" height="24" rx="5" />
      <path d="M23 27v-6a9 9 0 0 1 18 0v6" />
      <circle cx="32" cy="38" r="3" fill="currentColor" stroke="none" />
      <path d="M32 41v4" />
    </>
  ),
  complete: (
    <>
      <circle cx="32" cy="32" r="20" />
      <path d="m22 32 7 7 14-16" />
      <path d="M12 18 8 14m44 4 4-4M12 46l-4 4m44-4 4 4" opacity=".38" />
    </>
  ),
};

/** Meaningful, theme-safe micro-graphic for truthful state primitives. */
export function StateIllustration({
  variant = 'verified-empty',
  title,
  ...props
}: StateIllustrationProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      {...props}
    >
      <circle cx="32" cy="32" r="28" fill="currentColor" stroke="none" opacity=".055" />
      {paths[variant]}
    </svg>
  );
}
